import * as React from 'react';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { Form } from 'react-final-form';
import { withRouter } from 'next/router';
import { Button, Grid, Switch, Typography } from '@material-ui/core';
import { Share } from '@material-ui/icons';
import { withStyles } from '@material-ui/core/styles';
import { isNaN, debounce } from 'lodash';

import CompsSlider from './CompsSlider';
import * as types from '../../../../../actions/actionTypes';
import { getRelatedListings } from '../../../../../actions/comps';
import Messages, { MessagesTypes } from '../../../../utility/Messages';
import ButtonWithSnackBar from '../../../../utility/SnackBarButton';
import { ExitIcon } from '../../../../utility/Icons';
import track from '../../../../../lib/track';
import { isMobile } from '../../../../../lib/getDeviceSize';
import { addressString } from '../../../../../lib/helpers';
import { buttonStyles } from '../../../../../styles/base';
import combineStyles from '../../../../../styles/combineStyles';

import style from '../../../../../styles/sass/components/CompsChomper.module.scss';

export interface CompsFormProps {
  activeComps: any[];
  classes: any;
  dispatch: any;
  hideSearchButton?: boolean;
  initialFormState?: CompsFormState;
  listing: any;
  messages: MessagesTypes;
  onNewSearchPress: Function;
  onSubmit: Function;
  router: any;
  soldComps: any[];
  excludedListings: any[];
  startWithActiveComps: boolean;
  token: string;
  user: any;
}

export interface CompsFormState {
  bathRange: number[];
  brRange: number[];
  monthsOnMarket: number;
  lastUpdatedMaxMonths: number;
  lockRanges: boolean;
  maxBath: number;
  maxBr: number;
  maxPrice: number;
  maxSf: number;
  maxUnits: number;
  minBath: number;
  minBr: number;
  minPrice: number;
  minSf: number;
  minUnits: number;
  multiFamily: boolean;
  priceRange: number[];
  radius: number;
  sfRange: number[];
  soldComps: boolean;
  unitsRange: number[];
  width: number;
}

const styles = {
  adjust: {
    color: '#323232',
    fontSize: 12,
    margin: '5px 0 20px 8px',
    textAlign: 'left',
  },
  exitButton: {
    alignSelf: 'flex-end',
    marginRight: 10,
    marginTop: 10,
    minHeight: 20,
    minWidth: 20,
    padding: 0,
  },
  form: {
    flex: 1,
    margin: 0,
  },
  lockRangeSubParent: {
    width: '100%',
  },
  mobileFormButtons: {
    alignItems: 'center',
    backgroundColor: '#F5F5F5',
    display: 'flex',
    height: 100,
    justifyContent: 'space-between',
    paddingLeft: 25,
    paddingRight: 25,
    width: '100%',
  },
  rangeItem: {
    padding: '7px',
  },
  resetButton: {
    backgroundColor: 'transparent',
    color: '#323232',
    fontSize: 14,
    fontWeight: 'bold',
    letterSpacing: 1.3,
    textTransform: 'capitalize',
  },
  shareButton: {
    marginTop: 20,
  },
  submitButton: {
    backgroundColor: '#463264',
    color: '#fff',
    fontSize: 14,
    fontWeight: 'bold',
    height: 40,
    letterSpacing: 1.3,
    padding: 0,
    textTransform: 'capitalize',
    width: 125,
  },
  switchContainer: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
  },
  switchLabel: {
    color: '#0E1217',
    fontSize: 14,
    fontWeight: 'bold',
  },
};

const initialState: CompsFormState = {
  bathRange: [-1, 1],
  brRange: [-1, 1],
  lastUpdatedMaxMonths: 12,
  lockRanges: true,
  maxBath: 0,
  maxBr: 0,
  maxPrice: 0,
  maxSf: 0,
  maxUnits: 0,
  minBath: 0,
  minBr: 0,
  minPrice: 0,
  minSf: 0,
  minUnits: 0,
  monthsOnMarket: 12,
  multiFamily: false,
  priceRange: [-20, 20],
  radius: 1,
  sfRange: [-20, 20],
  soldComps: true,
  unitsRange: [-1, 1],
  width: 300,
};

export class CompsForm extends React.Component<CompsFormProps, CompsFormState> {
  supportsCopy =
    typeof navigator !== 'undefined' &&
    navigator.clipboard &&
    navigator.clipboard.writeText;
  listingBaths = 0;
  listingBr = 0;
  listingPrice = 0;
  listingSf = 0;
  listingUnits = 0;
  constructor(props: CompsFormProps) {
    super(props);
    this.state = {
      ...initialState,
      multiFamily: props.listing.propType === 'Multi-Family',
      soldComps: props.startWithActiveComps ? false : true,
    };
    this.loadComps = debounce(this.loadComps, 300);
  }

  componentDidMount() {
    this.listingBaths = parseInt(this.props.listing.noFullBaths, 10);
    this.listingBr = parseInt(this.props.listing.noBedrooms, 10);
    this.listingPrice = parseInt(this.props.listing.listPrice, 10);
    this.listingSf = parseInt(this.props.listing.squareFeet, 10);
    this.listingUnits = parseInt(this.props.listing.noUnits, 10) || 1;
    this.updateDimension();
    // If we started out with initial values, use them,
    // and fetch results. Otherwise, set them up
    if (
      this.props.initialFormState &&
      Object.keys(this.props.initialFormState).length > 0
    ) {
      this.setState(this.props.initialFormState, () => {
        this.loadComps(isMobile() ? false : true);
      });
    } else {
      this.calculateCompValues();
    }
  }

  reset = () => {
    this.setState(initialState);
  };

  updateDimension = () => {
    this.setState({
      width: window.innerWidth,
    });
  };

  calculateCompValues = () => {
    // TODO: this rerenders all of the sliders every time any one of them is changed.
    // We should only update what is required.
    this.setState(
      prevState => {
        return {
          maxBath: !isNaN(this.listingBaths)
            ? this.listingBaths + prevState.bathRange[1]
            : prevState.bathRange[1],
          maxBr: !isNaN(this.listingBr)
            ? this.listingBr + prevState.brRange[1]
            : prevState.brRange[1],
          maxPrice: !isNaN(this.listingPrice)
            ? Math.round(
                this.listingPrice +
                  this.listingPrice * (prevState.priceRange[1] / 100),
              )
            : prevState.priceRange[1],
          maxSf: !isNaN(this.listingSf)
            ? Math.round(
                this.listingSf + this.listingSf * (prevState.sfRange[1] / 100),
              )
            : prevState.sfRange[1],
          maxUnits: !isNaN(this.listingUnits)
            ? this.listingUnits + prevState.unitsRange[1]
            : prevState.unitsRange[1],
          minBath: !isNaN(this.listingBaths)
            ? this.listingBaths + prevState.bathRange[0]
            : prevState.bathRange[0],
          minBr: !isNaN(this.listingBr)
            ? this.listingBr + prevState.brRange[0]
            : prevState.brRange[0],
          minPrice: !isNaN(this.listingPrice)
            ? Math.round(
                this.listingPrice +
                  this.listingPrice * (prevState.priceRange[0] / 100),
              )
            : prevState.priceRange[0],
          minSf: !isNaN(this.listingSf)
            ? Math.round(
                this.listingSf + this.listingSf * (prevState.sfRange[0] / 100),
              )
            : prevState.sfRange[0],
          minUnits: !isNaN(this.listingUnits)
            ? this.listingUnits + prevState.unitsRange[0]
            : prevState.unitsRange[0],
        };
      },
      () => {
        if (!this.areCompsLoaded()) {
          this.loadComps(isMobile() ? false : true);
        }
      },
    );
  };

  areCompsLoaded = () => {
    if (this.state.soldComps && this.props.soldComps.length > 0) {
      return true;
    }
    if (!this.state.soldComps && this.props.activeComps.length > 0) {
      return true;
    }
    return false;
  };

  loadComps = (changeView: boolean = false) => {
    this.props.dispatch({ type: types.ClearComps });
    const minUnits = this.state.multiFamily ? this.state.minUnits : undefined;
    const maxUnits = this.state.multiFamily ? this.state.maxUnits : undefined;
    // If we're looking at active comps, use time on market
    let monthsOnMarket;
    if (this.state.monthsOnMarket && !this.state.soldComps) {
      // Convert months on market to days
      monthsOnMarket = this.state.monthsOnMarket * 30;
    }
    // If we're using sold comps, use updateDate
    let lastUpdatedMaxMonths;
    if (this.state.lastUpdatedMaxMonths && this.state.soldComps) {
      lastUpdatedMaxMonths = this.state.lastUpdatedMaxMonths;
    }

    this.props.dispatch(
      getRelatedListings(
        this.props.listing.id,
        this.state.soldComps,
        100, // limit
        this.state.minBath,
        this.state.maxBath,
        this.state.minBr,
        this.state.maxBr,
        this.state.minPrice,
        this.state.maxPrice,
        this.state.minSf,
        this.state.maxSf,
        minUnits,
        maxUnits,
        this.state.radius,
        monthsOnMarket,
        lastUpdatedMaxMonths,
      ),
    );
    if (changeView) {
      this.props.onSubmit();
    }
  };

  handleSubmit = () => {
    if (!this.props.token) {
      this.props.dispatch({
        postAuthPath: this.props.router.pathname,
        type: types.OpenSignupDialog,
      });
    } else {
      const minUnits = this.state.multiFamily ? this.state.minUnits : undefined;
      const maxUnits = this.state.multiFamily ? this.state.maxUnits : undefined;

      track.toolUsed('Comps Chomper', {
        lastUpdatedMaxMonths: this.state.lastUpdatedMaxMonths,
        listingId: this.props.listing.id,
        maxBath: this.state.maxBath,
        maxBr: this.state.maxBr,
        maxPrice: this.state.maxPrice,
        maxSf: this.state.maxSf,
        maxUnits,
        minBath: this.state.minBath,
        minBr: this.state.minBr,
        minPrice: this.state.minPrice,
        minSf: this.state.minSf,
        minUnits,
        monthsOnMarket: this.state.monthsOnMarket,
        radius: this.state.radius,
        soldComps: this.state.soldComps,
      });
      this.loadComps(true);
    }
  };

  handleChangeRange = (
    fieldName:
      | 'bathRange'
      | 'brRange'
      | 'priceRange'
      | 'sfRange'
      | 'unitsRange',
  ) => (event: any, newValue: any) => {
    this.setState(
      prevState => {
        let newRange = [];
        // If ranges aren't locked, allow a user to have separate mins and maxes
        if (!prevState.lockRanges) {
          newRange = newValue;
        } else if (newValue[0] !== prevState[fieldName][0]) {
          newRange = [newValue[0], -newValue[0]];
        } else if (newValue[1] !== prevState[fieldName][1]) {
          newRange = [-newValue[1], newValue[1]];
        } else {
          newRange = prevState[fieldName];
        }
        const newStateValue: any = {};
        newStateValue[fieldName] = newRange;
        return newStateValue;
      },
      () => this.calculateCompValues(),
    );
  };

  handleChangeValue = (
    fieldName: 'radius' | 'monthsOnMarket' | 'lastUpdatedMaxMonths',
  ) => (event: any, newValue: any) => {
    this.setState(prevState => {
      const newStateValue: any = {};
      newStateValue[fieldName] = newValue;
      return newStateValue;
    });
  };

  handleSwitch = (fieldName: 'soldComps' | 'lockRanges') => {
    this.setState(prevState => {
      const newStateValue: any = {};
      newStateValue[fieldName] = !prevState[fieldName];
      return newStateValue;
    });
  };

  // Copy the current URI to the clipboard, and stringify the
  // state to use with it so that another user can load it.
  shareSearch = () => {
    const excluded = this.props.excludedListings.map((listing: any) => {
      return listing.id;
    });
    const stringifiedState = JSON.stringify({
      ...this.state,
      excluded,
      listingId: this.props.listing.id,
    });
    const uri = encodeURI(
      `${window.location.origin}${window.location.pathname}?search=${stringifiedState}`,
    );
    navigator.clipboard.writeText(uri);
  };

  formatMonthsLabel = (count: number) => {
    if (count === 1) {
      return 'month';
    } else if (count > 1) {
      return 'months';
    }
    return '';
  };

  handleContactUs = () => {
    const { listing, token, user } = this.props;
    this.props.router.push({
      pathname: '/contact',
      query: token
        ? {
            email: user.email,
            location: listing.state,
            message: `Hi, I'd like more info on ${addressString(
              listing,
              false,
              true,
            )}.`,
            name: `${user.firstName} ${user.lastName}`,
          }
        : {},
    });
  };

  render() {
    const { classes, messages } = this.props;
    const {
      bathRange,
      brRange,
      monthsOnMarket,
      lastUpdatedMaxMonths,
      lockRanges,
      maxBath,
      maxBr,
      maxPrice,
      maxSf,
      maxUnits,
      minBath,
      minBr,
      minPrice,
      minSf,
      minUnits,
      multiFamily,
      priceRange,
      radius,
      sfRange,
      soldComps,
      unitsRange,
    } = this.state;
    return (
      <Form
        onSubmit={this.handleSubmit}
        render={({ handleSubmit }) => (
          <div className={style.rangeFormContainer} style={{ width: '100%' }}>
            <form style={styles.form}>
              <Messages messages={messages} />
              {!isMobile() && (
                <div
                  className={style.compsRangeHeader}
                  style={styles.rangeItem}
                >
                  <Typography component="div">
                    <Grid
                      component="label"
                      container
                      alignItems="center"
                      spacing={1}
                    >
                      <Grid item>Lock ranges</Grid>
                      <Grid item>
                        <Switch
                          checked={lockRanges}
                          onChange={() => this.handleSwitch('lockRanges')}
                          value="checkedB"
                          color="primary"
                          inputProps={{ 'aria-label': 'toggle range lock' }}
                        />
                      </Grid>
                    </Grid>
                  </Typography>
                  <div className={style.localRangeShareParent}>
                    {this.supportsCopy && (
                      <ButtonWithSnackBar
                        onSubmit={this.shareSearch}
                        buttonTitle={'Share'}
                        buttonIcon={<Share />}
                        variant="success"
                        snackBarMessage="Copied link to clipboard"
                      />
                    )}
                  </div>

                  <Typography component="div" className={style.compsSoldSwitch}>
                    <Grid
                      component="label"
                      container
                      alignItems="center"
                      spacing={1}
                    >
                      <Grid item>On-market comps</Grid>
                      <Grid item>
                        <Switch
                          checked={soldComps}
                          onChange={() => this.handleSwitch('soldComps')}
                          value="checkedB"
                          color="primary"
                          inputProps={{ 'aria-label': 'toggle active comps' }}
                        />
                      </Grid>
                      <Grid item>Sold comps</Grid>
                    </Grid>
                  </Typography>
                </div>
              )}
              <div className={style.rangeContainer} style={styles.rangeItem}>
                {isMobile() && (
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <Button
                      className={classes.exitButton}
                      onClick={() => this.props.onSubmit()}
                    >
                      <ExitIcon />
                    </Button>
                    <Typography className={classes.adjust}>
                      Adjust your settings to find the most accurate comps
                    </Typography>
                  </div>
                )}
                <div className={style.rangeSliderContainer}>
                  <CompsSlider
                    decimalScale={2}
                    label="Distance"
                    max={10}
                    min={0.5}
                    maxLabel={radius}
                    minLabel={0}
                    onChange={this.handleChangeValue('radius')}
                    shortLabel="mi"
                    step={0.25}
                    value={radius}
                  />
                  <CompsSlider
                    decimalScale={0}
                    label="Square footage"
                    max={100}
                    min={-100}
                    maxLabel={maxSf}
                    minLabel={minSf}
                    onChange={this.handleChangeRange('sfRange')}
                    percentage
                    shortLabel="sq ft"
                    formatUnitValue
                    step={5}
                    value={sfRange}
                  />
                  <CompsSlider
                    decimalScale={0}
                    label="Number of bedrooms"
                    max={this.listingBr}
                    min={0 - this.listingBr}
                    maxLabel={maxBr}
                    minLabel={minBr}
                    onChange={this.handleChangeRange('brRange')}
                    shortLabel={'beds'}
                    leftShortLabel={minBr === 1 ? 'bed' : undefined}
                    rightShortLabel={maxBr === 1 ? 'bed' : undefined}
                    step={1}
                    value={brRange}
                  />
                  <CompsSlider
                    decimalScale={0}
                    label="Number of bathrooms"
                    max={this.listingBaths}
                    min={0 - this.listingBaths}
                    maxLabel={maxBath}
                    minLabel={minBath}
                    onChange={this.handleChangeRange('bathRange')}
                    shortLabel="baths"
                    leftShortLabel={minBath === 1 ? 'bath' : undefined}
                    rightShortLabel={maxBath === 1 ? 'bath' : undefined}
                    step={1}
                    value={bathRange}
                  />
                  <CompsSlider
                    decimalScale={0}
                    label="Price range"
                    max={100}
                    min={-100}
                    maxLabel={maxPrice}
                    minLabel={minPrice}
                    onChange={this.handleChangeRange('priceRange')}
                    percentage
                    shortLabel="$"
                    step={5}
                    value={priceRange}
                  />
                  {soldComps ? (
                    <CompsSlider
                      decimalScale={0}
                      label="Sold within"
                      max={24}
                      min={1}
                      maxLabel={lastUpdatedMaxMonths}
                      minLabel={0}
                      onChange={this.handleChangeValue('lastUpdatedMaxMonths')}
                      shortLabel={this.formatMonthsLabel(lastUpdatedMaxMonths)}
                      step={1}
                      value={lastUpdatedMaxMonths}
                    />
                  ) : (
                    <CompsSlider
                      decimalScale={0}
                      label="Time on market"
                      max={12}
                      min={1}
                      maxLabel={monthsOnMarket}
                      minLabel={0}
                      onChange={this.handleChangeValue('monthsOnMarket')}
                      shortLabel={this.formatMonthsLabel(monthsOnMarket)}
                      step={1}
                      value={monthsOnMarket}
                    />
                  )}
                  {multiFamily && (
                    <CompsSlider
                      decimalScale={0}
                      label="Number of units"
                      max={this.listingUnits}
                      min={0 - this.listingUnits}
                      maxLabel={maxUnits}
                      minLabel={minUnits}
                      onChange={this.handleChangeRange('unitsRange')}
                      shortLabel="units"
                      step={1}
                      value={unitsRange}
                    />
                  )}
                </div>
                <div
                  style={
                    isMobile()
                      ? {
                          borderBottom: '1px solid #BCBCBC',
                          marginLeft: -7,
                          width: this.state.width,
                        }
                      : {}
                  }
                />
                {isMobile() && (
                  <React.Fragment>
                    <div
                      className={style.compsRangeHeader}
                      style={styles.rangeItem}
                    >
                      <div
                        className={classes.switchContainer}
                        style={{ marginTop: 5 }}
                      >
                        <Typography className={classes.switchLabel}>
                          Lock ranges
                        </Typography>
                        <Switch
                          checked={lockRanges}
                          onChange={() => this.handleSwitch('lockRanges')}
                          value="checkedB"
                          color="primary"
                          inputProps={{ 'aria-label': 'toggle range lock' }}
                        />
                      </div>
                      <div
                        style={
                          isMobile()
                            ? {
                                borderBottom: '1px solid #BCBCBC',
                                margin: '10px 0 10px -15px',
                                width: this.state.width,
                              }
                            : {}
                        }
                      />
                      <div className={classes.switchContainer}>
                        <Typography className={classes.switchLabel}>
                          On-market comps
                        </Typography>
                        <Switch
                          checked={soldComps}
                          onChange={() => this.handleSwitch('soldComps')}
                          value="checkedB"
                          color="primary"
                          inputProps={{ 'aria-label': 'toggle active comps' }}
                        />
                        <Typography className={classes.switchLabel}>
                          Sold comps
                        </Typography>
                      </div>
                    </div>
                    <div
                      style={
                        isMobile()
                          ? {
                              borderBottom: '1px solid #BCBCBC',
                              margin: '10px 0 10px -7px',
                              width: this.state.width,
                            }
                          : {}
                      }
                    />
                    <div style={{ margin: '15px 8px' }}>
                      <Typography
                        style={{
                          color: '#323232',
                          fontSize: 12,
                          textAlign: 'left',
                        }}
                      >
                        <strong>Note: </strong>All data is for informational
                        purposes only. For help pricing an offer on a home in
                        the current market,{' '}
                        <span
                          onClick={this.handleContactUs}
                          style={{ fontWeight: 'bold' }}
                        >
                          tap here to contact us.
                        </span>
                      </Typography>
                    </div>
                  </React.Fragment>
                )}
                {!isMobile() && (
                  <div className={style.buttonContainer}>
                    {!this.props.hideSearchButton && (
                      <div className={style.newSearchButton}>
                        <Button
                          variant="contained"
                          className={`${classes.buttonMediumPink}`}
                          onClick={() => this.props.onNewSearchPress()}
                        >
                          NEW SEARCH
                        </Button>
                      </div>
                    )}
                    <Button
                      variant="contained"
                      className={`${classes.buttonMediumGreen}`}
                      onClick={handleSubmit}
                    >
                      SUBMIT
                    </Button>
                  </div>
                )}
              </div>
              {isMobile() && (
                <div style={styles.mobileFormButtons}>
                  <Button onClick={this.reset} className={classes.resetButton}>
                    RESET
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    className={classes.submitButton}
                  >
                    SUBMIT
                  </Button>
                </div>
              )}
            </form>
          </div>
        )}
      />
    );
  }
}

const combinedStyles = combineStyles(buttonStyles, styles);

const mapStateToProps = (state: any) => ({
  activeComps: state.comps.relatedListings,
  messages: state.messages,
  soldComps: state.comps.comps,
  token: state.auth.token,
  user: state.auth.user,
});

export default connect(mapStateToProps)(
  withStyles(combinedStyles)(withRouter(CompsForm)),
);
